import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { canonicalProductUrl, internalMarketingPath } from '../../../utils/siteDomains'
import LocalePicker from '../../../i18n/LocalePicker.jsx'
import { localizeHref } from '../../../i18n/translate/routing.js'
import { pageSeoAddress } from '../../../i18n/translate/pageSeo.js'
import { useCurrentHostname, useCurrentPathname } from '../marketingPrerender'
import { appFeatures } from './featuresData'
import { getProfessionBySlug, professionCatalogue } from './professions/catalogue.js'
import {
  MARKETING_DEFAULT_IMAGE,
  MARKETING_SITE_URL,
  formatMarketingSeoTitle,
  getNewHubRouteSeo,
  servedMarketingPath,
} from './marketingSeo.js'

const legacyMarketingPathForHost = internalMarketingPath

// ─── SEO hook ─────────────────────────────────────────────────────────────────

export function useMeta(title: string, description: string) {
  useEffect(() => {
    const routeSeo = getNewHubRouteSeo(legacyMarketingPathForHost(window.location.pathname, window.location.hostname))
    const resolvedTitle = routeSeo ? formatMarketingSeoTitle(routeSeo.title) : title
    const resolvedDescription = routeSeo?.description || description
    const canonicalPath = servedMarketingPath(routeSeo?.canonicalPath || routeSeo?.path || window.location.pathname)
    const legacyCanonicalUrl = new URL(canonicalPath, MARKETING_SITE_URL).href
    // /de/pricing/ keeps its own address and locale (the English page is /pricing/).
    const { canonicalUrl, ogLocale } = pageSeoAddress(canonicalProductUrl(legacyCanonicalUrl) || legacyCanonicalUrl, window.location.pathname)
    const siteName = 'Offlico'
    const siteTitle = routeSeo ? formatMarketingSeoTitle(routeSeo.title, siteName) : formatMarketingSeoTitle(title, siteName)
    const imageUrl = new URL(routeSeo?.image || MARKETING_DEFAULT_IMAGE, MARKETING_SITE_URL).href
    const imageAlt = routeSeo?.imageAlt || routeSeo?.pageHeading || routeSeo?.title || 'Offlico for mobile service businesses'

    document.title = siteTitle || resolvedTitle
    const upsert = (attrs: Record<string, string>, content: string) => {
      const sel = Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join('')
      let el = document.querySelector<HTMLMetaElement>(`meta${sel}`)
      if (!el) {
        el = document.createElement('meta') as HTMLMetaElement
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v))
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }
    const upsertLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
      if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', rel)
        document.head.appendChild(el)
      }
      el.setAttribute('href', href)
    }

    upsert({ name: 'description' }, resolvedDescription)
    upsert({ property: 'og:title' }, siteTitle || resolvedTitle)
    upsert({ property: 'og:description' }, resolvedDescription)
    upsert({ property: 'og:type' }, 'website')
    upsert({ property: 'og:site_name' }, siteName)
    upsert({ property: 'og:locale' }, ogLocale)
    upsert({ property: 'og:url' }, canonicalUrl)
    upsert({ property: 'og:image' }, imageUrl)
    upsert({ property: 'og:image:alt' }, imageAlt)
    upsert({ name: 'twitter:card' }, 'summary_large_image')
    upsert({ name: 'twitter:title' }, siteTitle || resolvedTitle)
    upsert({ name: 'twitter:description' }, resolvedDescription)
    upsert({ name: 'twitter:image' }, imageUrl)
    upsert({ name: 'twitter:image:alt' }, imageAlt)
    upsertLink('canonical', canonicalUrl)
  }, [title, description])
}

// Shared tokens, icons, and primitives

export const C = {
  brand: '#2A6B5A',
  brandL: '#E6F2EE',
  brandM: '#A8D9CC',
  brandD: '#1d4f42',
  hub: '#3D52D5',
  hubL: '#EEF1FD',
  hubM: '#A5B4FC',
  hubD: '#2a3aaa',
  ink: '#111111',
  muted: '#5A6472',
  faint: '#9AA3AE',
  border: '#E4E7EC',
  surf: '#F7F8FA',
  white: '#ffffff',
  gold: '#F0A500',
}

export const APP_SIGN_IN_URL = '/signin?redirect=/app'
export const APP_SIGN_UP_URL = '/signup?redirect=/app'
export const APP_PRICING_URL = '/for-businesses/pricing'
export const APP_DESTINATION_URL = '/app'
export const PROFESSIONAL_UPGRADE_URL = APP_PRICING_URL
export const PROFESSIONAL_TRIAL_URL = '/for-businesses/pricing?cycle=monthly&checkout=basic&returnTo=%2Fapp'
// The sign-up form's profession keys (?profession=), by trade name or slug.
const PROFESSION_KEYS: Record<string, string> = {
  cleaning: 'cleaning',
  cleaner: 'cleaning',
  gardening: 'gardening',
  gardener: 'gardening',
  hairdressing: 'hairdressing',
  hairdresser: 'hairdressing',
  foot_care: 'foot_care',
  chiropodist: 'foot_care',
  podiatrist: 'foot_care',
  podiatry: 'foot_care',
  earwax: 'earwax',
  'earwax-removal': 'earwax',
  'ear-wax-removal': 'earwax',
}

const BUSINESS_PROFESSION_PATH_KEYS: Record<string, string> = {
  '/for-businesses/cleaners': 'cleaning',
  '/for-businesses/cleaner': 'cleaning',
  '/for-businesses/gardeners': 'gardening',
  '/for-businesses/gardener': 'gardening',
  '/for-businesses/mobile-hairdressers': 'hairdressing',
  '/for-businesses/mobile-hairdresser': 'hairdressing',
  '/for-businesses/chiropodists': 'foot_care',
  '/for-businesses/chiropodist': 'foot_care',
  '/for-businesses/ear-wax-removal-specialists': 'earwax',
  '/for-businesses/ear-wax-removal-specialist': 'earwax',
}

// The sign-up form, with the trade the visitor was reading about (Starter is free).
export const businessProfessionSignupUrl = (profession: string) => {
  const professionKey = PROFESSION_KEYS[profession]
  if (!professionKey) return APP_SIGN_UP_URL
  return `${APP_SIGN_UP_URL}&profession=${encodeURIComponent(professionKey)}`
}

// Preserve the trade selected on the landing page, then start the monthly
// Professional checkout after sign-up. Existing accounts use the same plan
// selection without being offered a second trial.
export function getProfessionalTrialUrl() {
  if (typeof window === 'undefined') return `/signup?redirect=${encodeURIComponent(PROFESSIONAL_TRIAL_URL)}`
  const profession = businessProfessionFromPath(legacyMarketingPathForHost(window.location.pathname, window.location.hostname))
  const professionParam = profession ? `&profession=${encodeURIComponent(profession)}` : ''
  return hasBusinessSession()
    ? PROFESSIONAL_TRIAL_URL
    : `/signup?redirect=${encodeURIComponent(PROFESSIONAL_TRIAL_URL)}${professionParam}`
}

export const businessProfessionFromPath = (pathname: string) => {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '')
  const match = Object.entries(BUSINESS_PROFESSION_PATH_KEYS).find(
    ([basePath]) => normalizedPath === basePath || normalizedPath.startsWith(`${basePath}/`),
  )
  return match?.[1] || ''
}

type LoggedInDestination = { kind: 'app'; label: string; href: string; color: string } | null

function hasStoredToken(key: string) {
  if (typeof window === 'undefined') return false
  try {
    return Boolean(window.localStorage?.getItem(key))
  } catch {
    return false
  }
}

// The address a link should use on this site: business pages at root paths on
// www.offlico.com, customer pages on localpickr.com. Same-site addresses stay
// relative; addresses on the other site are absolute.
export function directProductLink(path: string, origin = typeof window === 'undefined' ? '' : window.location.origin) {
  if (!origin || !path.startsWith('/')) return path
  const canonical = canonicalProductUrl(new URL(path, origin).href)
  if (!canonical) return path
  const url = new URL(canonical)
  return url.origin === origin ? url.pathname + url.search + url.hash : canonical
}

// "Get started" for businesses: the sign-up form, or the app for someone
// already signed in on this site.
export function getBusinessCtaUrl() {
  return directProductLink(hasBusinessSession() ? APP_DESTINATION_URL : APP_SIGN_UP_URL)
}

// Signed in to the business app (www.offlico.com) in this browser.
export function hasBusinessSession() {
  return hasStoredToken('scheduler.token')
}

export const UPGRADE_TO_PROFESSIONAL_LABEL = 'Upgrade to Professional'

// False on the first render, so the prerendered page and first paint show
// the signed-out wording.
export function useHasBusinessSession() {
  const [signedIn, setSignedIn] = useState(false)
  useEffect(() => {
    setSignedIn(hasBusinessSession())
  }, [])
  return signedIn
}

// The trial CTA is for Professional. Existing accounts are routed through
// plan selection, which checks eligibility before offering a trial.
export function StartFreeOrUpgradeLabel() {
  return <>{useHasBusinessSession() ? UPGRADE_TO_PROFESSIONAL_LABEL : 'Start 30-day free trial'}</>
}

export function startFreeOrUpgrade() {
  navigateTo(PROFESSIONAL_TRIAL_URL)
}

// ─── CTA refresh (28 Oct 2026) ────────────────────────────────────────────────
// Rithik, 28 Oct 2026: the Professional 30-day free trial stays the main
// button. Under it, in hero and final sections, the trial's terms and the free
// Starter plan (no card). Other sections end with a link that fits them, and
// links to pages Google should index are real <a href> to the served www
// address (no /for-businesses/ redirects).

// The served www address of a business marketing path, the same before and
// after JavaScript: '/for-businesses/chiropodists/online-booking' ->
// '/chiropodists/online-booking/', '/for-businesses' -> '/'. Sign-in, sign-up,
// app and file addresses are returned unchanged.
export function publicMarketingHref(path: string) {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return path
  const hashIndex = path.indexOf('#')
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ''
  const beforeHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const queryIndex = beforeHash.indexOf('?')
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : ''
  let pathname = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash
  if (/^\/(signup|signin|app|upgrade|book|c|admin)(\/|$)/.test(pathname)) return path
  if (pathname === '/for-businesses' || pathname === '/for-businesses/') pathname = '/'
  else if (pathname.startsWith('/for-businesses/')) pathname = pathname.slice('/for-businesses'.length)
  if (pathname !== '/' && !/\.[a-z0-9]+$/i.test(pathname)) pathname = `${pathname.replace(/\/+$/, '')}/`
  // On a translated page the link keeps the language (/de/pricing/).
  return localizeHref(`${pathname}${query}${hash}`)
}

// Free Starter sign-up (no card): the sign-up form, then the app, keeping the
// trade the visitor was reading about. Uses /signup/ (the sign-up address in
// the sitemap); the trial keeps PROFESSIONAL_TRIAL_URL.
export function getStarterSignupUrl(pathname?: string) {
  const path = pathname ?? (typeof window === 'undefined' ? '' : legacyMarketingPathForHost(window.location.pathname, window.location.hostname))
  const profession = businessProfessionFromPath(path)
  return `/signup/?redirect=%2Fapp${profession ? `&profession=${encodeURIComponent(profession)}` : ''}`
}

// Must match PagePricing's PROFESSIONAL_MONTHLY_PRICE (ctaContract.test.tsx).
// Weekday initials for calendar mock-ups, Monday first. They come from Intl
// rather than text, and the page translator writes each one in the page's
// language (i18n/translate/runtime.js, data-weekday).
const WEEKDAY_INITIALS = Array.from({ length: 7 }, (_, day) =>
  new Intl.DateTimeFormat('en-GB', { weekday: 'narrow', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 1 + day))),
)

export function WeekdayInitials() {
  return (
    <>
      {WEEKDAY_INITIALS.map((initial, day) => (
        <span key={day} data-weekday={day} translate="no">
          {initial}
        </span>
      ))}
    </>
  )
}

export const PROFESSIONAL_TRIAL_MONTHLY_PRICE = 29

// Keep the call sites stable while removing this copy from every marketing CTA.
export function TrialFinePrint(_props: { tone?: 'light' | 'dark'; align?: 'start' | 'center'; className?: string }) {
  return null
}

// Click handler for an <a href> marketing link: new-tab and modified clicks
// stay native; a plain click navigates inside the app like the old buttons.
export function followMarketingLink(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const target = event.currentTarget.getAttribute('target')
  if (target && target !== '_self') return
  event.preventDefault()
  navigateTo(href)
}

// A real link for something that used to be a <button onClick={navigateTo}>
// (feature cards, tiles, "Explore all features"): same classes, plus
// offlico-link-reset; the caller's CSS must also match the <a> (e.g.
// ".grid :is(button, a)").
export function MarketingLink({
  to, className, children, onClick, ...rest
}: { to: string; className?: string; children: React.ReactNode } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const href = publicMarketingHref(to)
  return (
    <a
      {...rest}
      className={['offlico-link-reset', className].filter(Boolean).join(' ')}
      href={href}
      onClick={(event) => {
        onClick?.(event)
        followMarketingLink(event, href)
      }}
    >
      {children}
    </a>
  )
}

// Ends a section with a link that fits it ("See how reminders work").
export function SectionLink({
  to, children, tone = 'light', className = '',
}: { to: string; children: React.ReactNode; tone?: 'light' | 'dark'; className?: string }) {
  return (
    <MarketingLink
      to={to}
      data-cta-kind="link"
      className={['offlico-section-link', `offlico-section-link--${tone}`, className].filter(Boolean).join(' ')}
    >
      <span>{children}</span>
      <IcoArrow />
    </MarketingLink>
  )
}

// The business app for someone signed in to it in this browser.
export function getLoggedInDestination(): LoggedInDestination {
  return hasBusinessSession() ? { kind: 'app', label: 'Offlico', href: APP_DESTINATION_URL, color: C.brand } : null
}

export function useLoggedInDestination() {
  const [destination, setDestination] = useState<LoggedInDestination>(() => getLoggedInDestination())
  useEffect(() => {
    const refresh = () => setDestination(getLoggedInDestination())
    refresh()
    window.addEventListener('storage', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [])
  return destination
}

const localRedesignRoutes = new Set([
  '/',
  '/upgrade',
  '/for-businesses/features',
  '/for-businesses/pricing',
  '/for-businesses/contact',
  '/for-businesses/switch',
  '/for-businesses',
  '/for-businesses/pricing',
  '/for-businesses/switch',
  '/for-businesses/features',
  '/for-businesses/contact',
  '/for-businesses/all-mobile-businesses',
  '/for-businesses/mobile-hairdressers',
  '/for-businesses/mobile-hairdressers/admin-overload',
  '/for-businesses/mobile-hairdressers/lead-generation',
  '/for-businesses/mobile-hairdressers/online-booking',
  '/for-businesses/mobile-hairdressers/appointment-reminders',
  '/for-businesses/mobile-hairdressers/client-records',
  '/for-businesses/mobile-hairdressers/practice-management-software',
  '/for-businesses/cleaners',
  '/for-businesses/cleaners/admin-overload',
  '/for-businesses/cleaners/lead-generation',
  '/for-businesses/cleaners/online-booking',
  '/for-businesses/cleaners/appointment-reminders',
  '/for-businesses/cleaners/client-records',
  '/for-businesses/cleaners/practice-management-software',
  '/for-businesses/gardeners',
  '/for-businesses/gardeners/admin-overload',
  '/for-businesses/gardeners/lead-generation',
  '/for-businesses/gardeners/online-booking',
  '/for-businesses/gardeners/appointment-reminders',
  '/for-businesses/gardeners/client-records',
  '/for-businesses/gardeners/practice-management-software',
  '/for-businesses/ear-wax-removal-specialists',
  '/for-businesses/ear-wax-removal-specialists/admin-overload',
  '/for-businesses/ear-wax-removal-specialists/lead-generation',
  '/for-businesses/ear-wax-removal-specialists/online-booking',
  '/for-businesses/ear-wax-removal-specialists/appointment-reminders',
  '/for-businesses/ear-wax-removal-specialists/client-records',
  '/for-businesses/ear-wax-removal-specialists/practice-management-software',
])

function isLocalBusinessProfessionRoute(path: string) {
  const match = path.match(/^\/for-businesses\/([^/]+)(?:\/.*)?$/)
  if (!match) return false
  return ['professions', 'chiropodists', 'chiropodist', 'mobile-hairdressers', 'mobile-hairdresser', 'cleaners', 'cleaner', 'gardeners', 'gardener', 'ear-wax-removal-specialists', 'ear-wax-removal-specialist'].includes(match[1])
    || Boolean(getProfessionBySlug(match[1]))
}

export function navigateTo(to: string, { skipPricing = false }: { skipPricing?: boolean } = {}) {
  if (!to) return
  let destination = to
  if (to === PROFESSIONAL_TRIAL_URL) {
    destination = getProfessionalTrialUrl()
  } else if (to === APP_SIGN_UP_URL && !skipPricing) {
    destination = hasBusinessSession()
      ? APP_DESTINATION_URL
      : businessProfessionSignupUrl(businessProfessionFromPath(legacyMarketingPathForHost(window.location.pathname, window.location.hostname)))
  }
  destination = localizeHref(directProductLink(destination))
  if (destination.startsWith('mailto:') || destination.startsWith('tel:') || destination.startsWith('http')) {
    window.location.href = destination
    return
  }
  const [pathWithQuery, hash] = destination.split('#')
  // Served addresses (/pricing on www) are matched by their internal address.
  const path = normalizeNavPath(legacyMarketingPathForHost(pathWithQuery.split('?')[0], window.location.hostname))
  const isLocalRedesignRoute = localRedesignRoutes.has(path) || isLocalBusinessProfessionRoute(path) || path.startsWith('/for-businesses/features/')
  if (isLocalRedesignRoute) {
    window.history.pushState({}, '', destination)
    window.dispatchEvent(new PopStateEvent('popstate'))
    if (hash) {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 80)
    } else {
      window.scrollTo(0, 0)
    }
    return
  }
  window.location.href = destination
}

// ─── Icons ────────────────────────────────────────────────────────────────────

export function IcoCheck({ color = C.brand, size = 14 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

export function IcoArrow({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  )
}

export function IcoStar() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill={C.gold}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
}

export function IcoMenu() {
  return <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
}

export function IcoClose() {
  return <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
}

export function IcoSearch() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
}

export function IcoCalendar() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
}
export function IcoUsers() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
}
export function IcoBell() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
}
export function IcoInvoice() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8" /></svg>
}
export function IcoCar() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h10l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" /><circle cx="7" cy="17" r="2" /><circle cx="17" cy="17" r="2" /></svg>
}
export function IcoFile() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M13 2v7h7" /></svg>
}
export function IcoChart() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6" /></svg>
}
export function IcoMapPin() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
}
export function IcoPhone() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.58 3.47 2 2 0 0 1 3.55 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.78a16 16 0 0 0 6.29 6.29l1.06-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
}

export function Logo({ size = 26, dark = false }: { size?: number; dark?: boolean }) {
  return (
    <img
      src="/public-assets/Offlico_icon-removebg-preview-fast.webp"
      alt="Offlico"
      width={size}
      height={size}
      style={{ display: 'block', width: size, height: size, objectFit: 'contain', filter: dark ? 'brightness(0) invert(1)' : undefined }}
    />
  )
}


// Footer of Offlico (business) pages: business links only.
export function SharedFooter() {
  const footerGroups = [
    ['Offlico', [['Home','/for-businesses'], ['Features','/for-businesses/features'], ['Pricing','/for-businesses/pricing'], ['Switch to Offlico','/for-businesses/switch'], ['Contact sales','/for-businesses/contact']]],
    ['Mobile businesses', [['All mobile businesses','/for-businesses/all-mobile-businesses'], ['Browse professions','/for-businesses/professions'], ['Chiropodists and podiatrists','/for-businesses/chiropodists'], ['Mobile hairdressers','/for-businesses/mobile-hairdressers'], ['Cleaners','/for-businesses/cleaners'], ['Gardeners','/for-businesses/gardeners'], ['Ear wax removal specialists','/for-businesses/ear-wax-removal-specialists']]],
    ['Resources', [['Blog','/blog'], ['Free tools','/for-businesses/tools'], ['Templates','/templates'], ['Support','/support']]],
    ['Legal', [['Privacy policy','/privacy'], ['Terms of service','/terms']]],
  ] as const

  return (
    <footer style={{ borderTop: `1px solid ${C.border}`, background: C.white, padding: '48px 24px 28px' }}>
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.35fr repeat(4, 1fr)', gap: 32, marginBottom: 34 }} className="footer-grid">
          <div>
            <a href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 14 }}>
              <Logo />
              <span style={{ fontWeight: 800, fontSize: 16, color: C.ink, letterSpacing: '-0.02em' }}>Offlico</span>
            </a>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, maxWidth: 260 }}>
              Business software for mobile and home-visit professionals: bookings, reminders, payments and admin in one place.
            </p>
          </div>
          {footerGroups.map(([heading, links]) => (
            <div key={heading}>
              <p style={{ fontSize: 12, fontWeight: 800, color: C.ink, marginBottom: 12 }}>{heading}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {links.map(([label, href]) => (
                  <a key={label} href={publicMarketingHref(href)} style={{ fontSize: 12.5, color: C.faint, textDecoration: 'none' }}>{label}</a>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: '#C0C7D0', borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>© 2026 Offlico Ltd. All rights reserved.</p>
      </div>
    </footer>
  )
}

// ─── Window chrome wrapper ────────────────────────────────────────────────────

export function WindowChrome({ url, children }: { url: string; children: React.ReactNode }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 64px rgba(0,0,0,0.1)', fontFamily: 'Figtree, system-ui, sans-serif' }}>
      <div style={{ background: C.surf, borderBottom: `1px solid ${C.border}`, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 5 }}>
        {['#FF5F57','#FEBC2E', C.hub].map(c => <span key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />)}
        <span style={{ marginLeft: 8, fontSize: 10.5, color: C.faint, fontFamily: 'DM Mono, monospace' }}>{url}</span>
      </div>
      {children}
    </div>
  )
}

function LeadChoiceReviewIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 4h8.5L19 7.5V20H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
      <path d="M15 4v4h4" />
      <path d="M8.5 10.5h5.5M8.5 14h3.2" />
      <path d="m14.2 16.9 3.7-3.7 1.9 1.9-3.7 3.7-2.4.5.5-2.4Z" />
    </svg>
  )
}

function LeadChoiceCalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3.5v3M16 3.5v3M4 10h16" />
    </svg>
  )
}

function LeadChoiceCardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6.5" width="16" height="11" rx="2" />
      <path d="M4 10h16M8 14h3" />
    </svg>
  )
}

export function LocalLeadChoiceSection({
  compact = false,
  requestTitle = 'Routine foot care · Guildford GU1',
  requestDetail = 'Home visit for an older parent. Flexible weekday appointment.',
}: {
  compact?: boolean
  requestTitle?: string
  requestDetail?: string
}) {
  return (
    <section className={`local-lead-choice-section${compact ? ' local-lead-choice-section--compact' : ''}`} aria-labelledby={compact ? 'local-lead-choice-compact-title' : 'local-lead-choice-title'}>
      <div className="local-lead-choice-inner">
        <div className="local-lead-choice-copy">
          <p className="local-lead-choice-kicker">Local customer requests</p>
          <h2 id={compact ? 'local-lead-choice-compact-title' : 'local-lead-choice-title'}>
            Could you take on more clients?
          </h2>
          <p>
            See nearby customer requests and contact the ones that fit your working day.
          </p>
        </div>

        <div className="local-lead-choice-flow" aria-label="Review, contact and book lead flow">
          {[
            { label: 'Review', icon: LeadChoiceReviewIcon },
            { label: 'Contact', icon: IcoPhone },
            { label: 'Book', icon: LeadChoiceCalendarIcon },
          ].map((step, index) => {
            const Icon = step.icon
            return (
              <div className="local-lead-choice-flow-step" key={step.label}>
                <span className="local-lead-choice-flow-icon"><Icon /></span>
                <strong>{step.label}</strong>
                {index < 2 && <IcoArrow size={30} />}
              </div>
            )
          })}
        </div>

        <div className="local-lead-choice-card" aria-label="Example Offlico customer request">
          <div className="local-lead-choice-card-head">
            <div>
              <p>New customer request</p>
              <h3>{requestTitle}</h3>
            </div>
            <span>2.1 miles away</span>
          </div>

          <div className="local-lead-choice-detail-grid">
            <div>
              <IcoMapPin />
              <span>Guildford GU1</span>
            </div>
            <div>
              <ClockMiniIcon />
              <span>This week · mornings preferred</span>
            </div>
            <div>
              <LeadChoiceCardIcon />
              <span>Matches your area</span>
            </div>
          </div>

          <div className="local-lead-choice-card-bottom">
            <div>
              <IcoUsers />
              <span>{requestDetail}</span>
            </div>
            <button type="button">
              Contact lead
              <IcoArrow />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function ClockMiniIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  )
}

// ─── Shared nav ───────────────────────────────────────────────────────────────

const featureNavChildren = appFeatures.map(feature => ({
  label: feature.title,
  href: `/for-businesses/features/${feature.slug}`,
  emoji: feature.emoji,
}))

const businessTypeNavChildren = [
  {
    label: 'Browse all professions',
    href: '/for-businesses/professions',
  },
  {
    label: 'Chiropodists and podiatrists',
    href: '/for-businesses/chiropodists',
  },
  {
    label: 'Mobile hairdressers',
    href: '/for-businesses/mobile-hairdressers',
  },
  {
    label: 'Cleaners',
    href: '/for-businesses/cleaners',
  },
  {
    label: 'Gardeners',
    href: '/for-businesses/gardeners',
  },
  {
    label: 'Ear wax removal specialists',
    href: '/for-businesses/ear-wax-removal-specialists',
  },
]

function matchingProfessionNavResults(query: string) {
  const term = query.trim().toLocaleLowerCase('en-GB')
  if (!term) return []
  return professionCatalogue
    .filter(profession => [profession.name, ...profession.searchTerms].some(value => value.toLocaleLowerCase('en-GB').includes(term)))
    .sort((a, b) => {
      const aName = a.name.toLocaleLowerCase('en-GB')
      const bName = b.name.toLocaleLowerCase('en-GB')
      const aRank = aName.startsWith(term) ? 0 : aName.includes(term) ? 1 : 2
      const bRank = bName.startsWith(term) ? 0 : bName.includes(term) ? 1 : 2
      return aRank - bRank || aName.localeCompare(bName, 'en-GB')
    })
    .slice(0, 6)
}

const appSubNav = [
  { label: 'Home', desc: 'Overview of the Offlico App.', href: '/for-businesses' },
  {
    label: 'Mobile business types',
    desc: 'Profession-specific Offlico pages.',
    href: '/for-businesses/all-mobile-businesses',
    children: businessTypeNavChildren,
    childrenGridColumns: 2,
    dropdownMinWidth: 520,
    footerLabel: 'View all mobile business types',
  },
  { label: 'Features', desc: 'What Offlico does.', href: '/for-businesses/features', children: featureNavChildren },
  { label: 'Pricing', desc: 'What it costs.', href: '/for-businesses/pricing' },
  { label: 'Switch to Offlico', desc: 'Migrate from your current system.', href: '/for-businesses/switch' },
  { label: 'Contact', desc: 'Ask questions or get help.', href: '/for-businesses/contact' },
]

function normalizeNavPath(path: string) {
  if (path.length > 1 && path.endsWith('/')) {
    return path.replace(/\/+$/, '')
  }
  return path
}

type SubNavChild = { label: string; href: string; emoji?: string }
type SubNavItem = {
  label: string;
  desc: string;
  href: string;
  children?: SubNavChild[];
  childrenGridColumns?: number;
  dropdownMinWidth?: number;
  footerLabel?: string;
}

function SiteBrand() {
  return (
    <>
      <Logo />
      <span>Offlico</span>
    </>
  )
}

// Header of Offlico's pages: business links only. (LocalPickr's pages and
// header are in its own repo, Rithik-bot/localpickr.)
export function SharedNav({
  active: activeProp, accentColor: accentColorProp, accentBg = C.brandL, currentPathOverride, hideAccountActions = false,
}: {
  active?: 'app'; accentColor?: string; accentBg?: string; currentPathOverride?: string; hideAccountActions?: boolean;
}) {
  const active: 'app' = activeProp || 'app'
  const accentColor = accentColorProp || C.brand
  const brandName = 'Offlico'
  const nav = (href: string) => {
    navigateTo(href)
  }
  const [open, setOpen] = useState(false)
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null)
  const [mobileExpandedLabel, setMobileExpandedLabel] = useState<string | null>(null)
  const [professionQuery, setProfessionQuery] = useState('')
  const mobileDrawerRef = useRef<HTMLElement | null>(null)
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null)

  const subNav = appSubNav as SubNavItem[]
  const signInUrl = APP_SIGN_IN_URL
  const primaryCtaUrl = PROFESSIONAL_TRIAL_URL
  const primaryCtaLabel = 'Create account'
  const loggedInDestination = useLoggedInDestination()
  const currentPathname = useCurrentPathname()
  const currentHostname = useCurrentHostname()
  const currentPath = normalizeNavPath(currentPathOverride || legacyMarketingPathForHost(currentPathname, currentHostname))
  const professionResults = matchingProfessionNavResults(professionQuery)
  const hasExactSubMatch = subNav.some(item => normalizeNavPath(item.href.split('#')[0]) === currentPath)
    || isLocalBusinessProfessionRoute(currentPath)
  const isSubActive = (href: string) => {
    const path = normalizeNavPath(href.split('#')[0])
    if (path === '/') return false
    if (currentPath === path) return true
    if (path === '/for-businesses/all-mobile-businesses' && isLocalBusinessProfessionRoute(currentPath)) return true
    // Prefix match only when no sub-nav item exactly matches currentPath
    // (e.g. feature detail pages should still highlight "Features")
    if (!hasExactSubMatch && currentPath.startsWith(path + '/')) return true
    return false
  }

  const closeMobileMenu = () => {
    setOpen(false)
    setMobileExpandedLabel(null)
  }

  useEffect(() => {
    if (!open) return undefined

    const body = document.body
    const root = document.documentElement
    const previousBodyOverflow = body.style.overflow
    const previousRootOverflow = root.style.overflow
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null

    body.style.overflow = 'hidden'
    root.style.overflow = 'hidden'

    const drawer = mobileDrawerRef.current
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const focusable = () => Array.from(drawer?.querySelectorAll<HTMLElement>(focusableSelector) || [])
    focusable()[0]?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeMobileMenu()
        return
      }
      if (event.key !== 'Tab') return

      const items = focusable()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const handleResize = () => {
      if (window.innerWidth > 740) closeMobileMenu()
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('resize', handleResize)

    return () => {
      body.style.overflow = previousBodyOverflow
      root.style.overflow = previousRootOverflow
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleResize)
      if (previousFocus?.isConnected) previousFocus.focus()
      else mobileMenuButtonRef.current?.focus()
    }
  }, [open])

  return (
    <header className={`offlico-site-header${active ? ' offlico-site-header--audience' : ''}`} style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(14px)', borderBottom: `1px solid ${C.border}` }}>
      {/* Main row */}
      <div className="nav-main-row" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 24px', height: 52, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
        {/* Left: logo */}
        <button className="offlico-site-header__home" aria-label={`${brandName} home`} onClick={() => nav('/for-businesses')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, justifySelf: 'start' }}>
          <Logo />
          <span style={{ fontWeight: 800, fontSize: 17, color: C.ink, letterSpacing: '-0.025em' }}>Offlico</span>
        </button>

        {/* Right: the country flag and the menu button (narrow screens) */}
        <div className="nav-main-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, justifySelf: 'end' }}>
          <span className="offlico-locale-slot"><LocalePicker placement="header" /></span>
          <button
            ref={mobileMenuButtonRef}
            type="button"
            aria-label="Open navigation menu"
            aria-expanded={open}
            aria-controls="offlico-mobile-navigation"
            onClick={() => setOpen(current => !current)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: C.ink, padding: 0 }}
            className="nav-m offlico-mobile-menu-trigger"
          >
            <IcoMenu />
          </button>
        </div>
      </div>

      {/* Sub-nav row */}
      {active && <div style={{ background: C.white, position: 'relative' }} className="subnav-d">
        <div className="offlico-audience-header">
          <button className="offlico-site-header__home offlico-audience-header__brand" aria-label={`${brandName} home`} onClick={() => nav('/for-businesses')}>
            <SiteBrand />
          </button>
          <nav className="offlico-audience-header__links" aria-label="Business pages">
          {subNav.map(({ label, href, children, childrenGridColumns, dropdownMinWidth, footerLabel }) => {
            const isActive = isSubActive(href)
            const isHovered = hoveredLabel === label
            const isProfessionMenu = label === 'Mobile business types'
            const dropdownColumns = childrenGridColumns || 2
            return (
              <div key={label} style={{ position: 'relative', display: 'flex', alignItems: 'stretch' }}
                onMouseEnter={() => setHoveredLabel(label)}
                onMouseLeave={() => setHoveredLabel(null)}
                onFocus={() => setHoveredLabel(label)}
                onBlur={event => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) setHoveredLabel(null)
                }}
              >
                <button onClick={() => nav(href)} style={{
                  all: 'unset',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '0 18px',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: isActive ? accentColor : (isHovered && !isActive ? C.ink : C.muted),
                  borderBottom: `2px solid ${isActive ? accentColor : (isHovered && !isActive ? C.border : 'transparent')}`,
                  transition: 'color 0.15s, border-color 0.15s',
                }}>
                  {label}
                  {children && (
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ marginTop: 1, opacity: 0.5 }}>
                      <path d="M1.5 3.5L4.5 6.5L7.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>

                {/* Dropdown */}
                {children && isHovered && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: 1,
                    background: C.white,
                    border: `1px solid ${C.border}`,
                    borderRadius: 11,
                    boxShadow: '0 8px 28px rgba(0,0,0,0.10)',
                    minWidth: dropdownMinWidth || 430,
                    maxWidth: 'calc(100vw - 48px)',
                    padding: 6,
                    zIndex: 200,
                  }}>
                    {isProfessionMenu && <div className="offlico-profession-nav-search">
                      <label htmlFor="offlico-profession-search-desktop">Find your profession</label>
                      <input
                        id="offlico-profession-search-desktop"
                        type="search"
                        value={professionQuery}
                        onChange={event => setProfessionQuery(event.target.value)}
                        placeholder="Search all professions"
                        autoComplete="off"
                      />
                    </div>}
                    {isProfessionMenu && professionQuery.trim() ? (
                      <div className="offlico-profession-nav-results" aria-live="polite">
                        {professionResults.length ? professionResults.map(profession => (
                          <button
                            key={profession.id}
                            type="button"
                            onClick={() => { nav(profession.internalHref); setHoveredLabel(null); setProfessionQuery('') }}
                          >
                            <span>{profession.name}</span>
                            <small>{profession.category}</small>
                          </button>
                        )) : <p>No matching profession. Browse the full list below.</p>}
                      </div>
                    ) : <div style={{
                      display: dropdownColumns > 1 ? 'grid' : 'block',
                      gridTemplateColumns: dropdownColumns > 1 ? `repeat(${dropdownColumns}, minmax(0, 1fr))` : undefined,
                      gap: dropdownColumns > 1 ? 2 : undefined,
                    }}>
                      {children.map(({ label: cl, href: ch, emoji }) => (
                        <button key={cl} onClick={() => { nav(ch); setHoveredLabel(null) }}
                          style={{ all: 'unset', display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, color: C.ink, boxSizing: 'border-box', whiteSpace: 'nowrap' }}
                          onMouseEnter={e => (e.currentTarget.style.background = C.brandL)}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          {emoji ? <span style={{ fontSize: 17 }}>{emoji}</span> : null}
                          {cl}
                        </button>
                      ))}
                    </div>}
                    {isProfessionMenu && professionQuery.trim() && <button
                      type="button"
                      className="offlico-profession-nav-browse"
                      onClick={() => { nav('/for-businesses/professions'); setHoveredLabel(null); setProfessionQuery('') }}
                    >
                      Browse all professions →
                    </button>}
                    <div style={{ height: 1, background: C.border, margin: '4px 8px' }} />
                    <button onClick={() => { nav(href); setHoveredLabel(null) }}
                      style={{ all: 'unset', display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: accentColor, boxSizing: 'border-box' }}
                      onMouseEnter={e => (e.currentTarget.style.background = C.brandL)}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      {footerLabel || 'View all features'} →
                    </button>
                  </div>
                )}
              </div>
            )
          })}
          </nav>
          {!hideAccountActions && <div className="offlico-audience-header__actions">
            <LocalePicker placement="header" />
            {loggedInDestination ? (
              <button onClick={() => navigateTo(loggedInDestination.href)} style={{ fontSize: 13, fontWeight: 600, color: C.white, background: loggedInDestination.color, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {loggedInDestination.label}
              </button>
            ) : (
              <>
                <button onClick={() => navigateTo(signInUrl)} style={{ fontSize: 13, fontWeight: 500, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: '7px 10px', whiteSpace: 'nowrap' }}>Log in</button>
                <button data-cta-kind="trial" onClick={() => navigateTo(primaryCtaUrl, { skipPricing: true })} style={{ fontSize: 13, fontWeight: 600, color: C.white, background: accentColor, padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>{primaryCtaLabel}</button>
              </>
            )}
          </div>}
        </div>
      </div>}

      {open && typeof document !== 'undefined' && createPortal(
        <div className="offlico-mobile-navigation-layer">
          <button type="button" className="offlico-mobile-navigation-backdrop" aria-label="Close navigation menu" onClick={closeMobileMenu} />
          <aside
            ref={mobileDrawerRef}
            id="offlico-mobile-navigation"
            className="offlico-mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label={`${brandName} navigation`}
          >
            <div className="offlico-mobile-navigation__header">
              <button type="button" className="offlico-mobile-navigation__brand" aria-label={`${brandName} home`} onClick={() => { nav('/for-businesses'); closeMobileMenu() }}>
                <SiteBrand />
              </button>
              <button type="button" className="offlico-mobile-navigation__close" aria-label="Close navigation menu" onClick={closeMobileMenu}>
                <IcoClose />
              </button>
            </div>

            <nav className="offlico-mobile-navigation__links" aria-label="Business pages">
              {subNav.map(({ label, href, children }) => {
                const expanded = mobileExpandedLabel === label
                const isProfessionMenu = label === 'Mobile business types'
                return (
                  <div className="offlico-mobile-navigation__group" key={label}>
                    <div className="offlico-mobile-navigation__row">
                      <button
                        type="button"
                        className={isSubActive(href) ? 'is-active' : ''}
                        aria-current={isSubActive(href) ? 'page' : undefined}
                        onClick={() => { nav(href); closeMobileMenu() }}
                      >
                        {label}
                      </button>
                      {children && (
                        <button
                          type="button"
                          className="offlico-mobile-navigation__expand"
                          aria-label={`${expanded ? 'Hide' : 'Show'} ${label} options`}
                          aria-expanded={expanded}
                          onClick={() => setMobileExpandedLabel(current => current === label ? null : label)}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M3.5 6L8 10.5 12.5 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {children && expanded && (
                      <div className="offlico-mobile-navigation__children">
                        {isProfessionMenu && <div className="offlico-profession-nav-search offlico-profession-nav-search--mobile">
                          <label htmlFor="offlico-profession-search-mobile">Find your profession</label>
                          <input
                            id="offlico-profession-search-mobile"
                            type="search"
                            value={professionQuery}
                            onChange={event => setProfessionQuery(event.target.value)}
                            placeholder="Search all professions"
                            autoComplete="off"
                          />
                        </div>}
                        {isProfessionMenu && professionQuery.trim() ? (
                          <div className="offlico-profession-nav-results offlico-profession-nav-results--mobile" aria-live="polite">
                            {professionResults.length ? professionResults.map(profession => (
                              <button key={profession.id} type="button" onClick={() => { nav(profession.internalHref); setProfessionQuery(''); closeMobileMenu() }}>
                                <span>{profession.name}</span>
                                <small>{profession.category}</small>
                              </button>
                            )) : <p>No matching profession. Browse the full list below.</p>}
                          </div>
                        ) : children.map(({ label: childLabel, href: childHref, emoji }) => (
                          <button type="button" key={childLabel} onClick={() => { nav(childHref); closeMobileMenu() }}>
                            {emoji ? <span aria-hidden="true">{emoji}</span> : null}
                            {childLabel}
                          </button>
                        ))}
                        {isProfessionMenu && professionQuery.trim() && <button type="button" onClick={() => { nav('/for-businesses/professions'); setProfessionQuery(''); closeMobileMenu() }}>Browse all professions →</button>}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>

            <div className="offlico-mobile-navigation__locale"><LocalePicker placement="drawer" /></div>
            {!hideAccountActions && <div className="offlico-mobile-navigation__account">
              {loggedInDestination ? (
                <button type="button" className="offlico-mobile-navigation__primary" style={{ background: loggedInDestination.color }} onClick={() => { navigateTo(loggedInDestination.href); closeMobileMenu() }}>
                  {loggedInDestination.label}
                </button>
              ) : (
                <>
                  <button type="button" className="offlico-mobile-navigation__secondary" onClick={() => { navigateTo(signInUrl); closeMobileMenu() }}>Log in</button>
                  <button type="button" data-cta-kind="trial" className="offlico-mobile-navigation__primary" style={{ background: accentColor }} onClick={() => { navigateTo(primaryCtaUrl, { skipPricing: true }); closeMobileMenu() }}>{primaryCtaLabel}</button>
                </>
              )}
            </div>}
          </aside>
        </div>,
        document.body,
      )}
    </header>
  )
}
